import React, { useEffect, useState } from "react";
import SVG from "react-inlinesvg";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import SimpleTable from "my-simple-react-table";
import { toAbsoluteUrl } from "../../../../../../../../../../_metronic/_helpers";
import { formatAmount, formatDate, formatStatus, permissionCssClass, getOrderBalanceAfter } from "../../../../../../../../../util/Helpers";
import ViewOrderModal from "../../../../../../../../OrderManagement/pages/modals/ViewOrderModal";
import CancelOrderModal from "../../../../../../../../OrderManagement/pages/modals/CancelOrderModal";
import NewOrderModal from "../../../../../../../../OrderManagement/pages/modals/NewOrderModal";
import { Permissions, PermissionType } from "../../../../../../../../../util/Permissions";
import EditOrderModal from "../../../../../../../../OrderManagement/pages/modals/EditOrderModal";
import { FilterGroup } from "../../../../../../../../Common/components/FilterGroup";
import { useDownloadOrdersReportMutation } from "../../../../../../../../../../redux/apiSlice";
import cogoToast from "cogo-toast";
import * as selectedUserHandlers from "../../../../../../../_redux/selected-user/selectedUserHandlers";

const Orders = props => {

    const { PUBLIC_URL } = process.env;
    const adminUser = useSelector(state => state.auth.user);
    /* Get Account Details from redux */
    const accountDetails = useSelector(({ selectedUser }) => {
        return selectedUser.data?.accounts.filter(acc => acc.accountId === props.match.params.accountId)[0]?.details;
    }, shallowEqual);

    const dispatch = useDispatch();
    const { userId, accountId } = props.match.params;

    // Fetch orders when component mounts or userId/accountId changes
    useEffect(() => {
        dispatch(selectedUserHandlers.fetchUserAccountDetails(userId, accountId));
    }, [dispatch, userId, accountId]);

    const refreshOrders = () => {
        dispatch(selectedUserHandlers.fetchUserAccountDetails(userId, accountId));
    };

    /* Order Modal State Management */
    const [orderModal, setOrderModal] = useState({
        showNewOrderModal: false,
        showViewOrderModal: false,
        showCancelOrderModal: false,
        showEditOrderModal: false,
        details: null
    });

    /* Modal show/hide Toggle */
    const toggleModal = modal => _ => {
        setOrderModal({ ...orderModal, [modal]: !orderModal[modal] })
    }

    /* View Order Details */
    const viewOrder = id => {
        const details = accountDetails.orders.filter(order => id === order.id)[0];
        setOrderModal({ ...orderModal, showViewOrderModal: true, details });
    }

    /* Edit Order Modal*/
    const editOrder = details => {
        setOrderModal({
            ...orderModal,
            showEditOrderModal: true,
            details
        })
    }

    /* Cancel Order Modal */
    const cancelOrder = id => {
        const details = accountDetails.orders.filter(order => id === order.id)[0];
        setOrderModal({ ...orderModal, showCancelOrderModal: true, details });
    }

    const formatActivity = row => {
        return <div className="d-flex align-items-center" style={{ whiteSpace: "nowrap" }}>
            <span className={`svg-icon svg-icon-lg svg-icon-${row.side === "BUY" ? "primary" : "danger"} mr-3`}>
                <SVG
                    title=""
                    src={toAbsoluteUrl("/media/svg/icons/Shopping/Cart2.svg")}
                />
            </span>
            <div>
                <a href={`${PUBLIC_URL}/instruments/${row?.instrumentId}`} rel="noopener noreferrer"
                    target="_blank" className="text-dark-75 font-weight-bolder text-hover-primary mb-1 font-size-lg">
                    {row.symbol}
                    <span className="text-muted pl-2">
                        - {row.side}
                    </span>
                </a>
            </div>
        </div>
    }

    const orderAction = row => {

        return (
            <span style={{ whiteSpace: "nowrap" }}>
                <button
                    className="btn btn-icon btn-white btn-hover-primary btn-sm mx-1"
                    onClick={_ => viewOrder(row.id)}

                >
                    <span className="svg-icon svg-icon-md svg-icon-primary">
                        <SVG
                            title="View Order"
                            src={toAbsoluteUrl("/media/svg/icons/General/Eye-2.svg")}
                        />
                    </span>
                </button>

                <button
                    title="Edit Order"
                    className={`btn btn-icon btn-light btn-hover-primary btn-sm mx-3 ${permissionCssClass(adminUser, Permissions.ORDERS, PermissionType.UPDATE)}`}
                    onClick={_ => editOrder(row)}
                    disabled={(row.status !== "PENDING" && row.status !== "PROCESSING" && row.status !== "CANCELLING" && row.status !== "MANUAL_PROCESSING")}
                >
                    <span className={`svg-icon svg-icon-md svg-icon-${(row.status !== "PENDING" && row.status !== "PROCESSING" && row.status !== "CANCELLING" && row.status !== "MANUAL_PROCESSING") ? "muted" : "primary"}`}>
                        <SVG
                            title="Edit Order"
                            src={toAbsoluteUrl("/media/svg/icons/Communication/Write.svg")}
                        />
                    </span>
                </button>

                <button
                    disabled={(row.status !== "PENDING" && row.status !== "PROCESSING")}
                    className={`btn btn-icon btn-white btn-hover-danger btn-sm ${permissionCssClass(adminUser, Permissions.ORDERS, PermissionType.DELETE)}`}
                    onClick={_ => cancelOrder(row.id)}
                >
                    <span
                        className={`svg-icon svg-icon-md svg-icon-${(row.status !== "PENDING" && row.status !== "PROCESSING") ? "muted" : "danger"}`}>
                        <SVG
                            title="Cancel Order"
                            src={toAbsoluteUrl("/media/svg/icons/Code/Error-circle.svg")}
                        />
                    </span>
                </button>
            </span>
        )
    }

    const [
        downloadOrdersReport,
        {
            isLoading: isLoadingReport,
            isSuccess: isReportGenerating,
            error: errorReport,
        }
    ] = useDownloadOrdersReportMutation(accountDetails.userGroup)

    useEffect(() => {
        if (errorReport) {
            cogoToast.error(errorReport.data);
        }
    }, [errorReport])

    useEffect(() => {
        if (isReportGenerating) {
            cogoToast.success(`Orders export in progress. The resulting file will be sent to your mail.`);
        }
    }, [isReportGenerating])


    return (
        <div className="row">
            <div className="col-12">
                {/* <!--begin::List Widget 14--> */}
                <div className="card card-custom gutter-b">
                    <FilterGroup defaultFilters={{ endDate: new Date().toISOString().split('T')[0] }}>
                        {/* <!--begin::Header--> */}
                        <div className="card-header border-0">

                            {/* <!--begin::Title--> */}
                            <div className="card-title pt-5 w-100">
                                <h3 className="card-label">
                                    <span className="d-block text-dark h3 font-weight-bolder">Orders</span>
                                    <span className="d-block text-muted mt-2 font-size-sm">Buying and Selling of
                                        Assets</span>
                                </h3>
                                <div
                                    className={`card-toolbar ml-auto ${permissionCssClass(adminUser, Permissions.ORDERS, PermissionType.CREATE)}`}>
                                    <button
                                        className="btn btn-primary font-weight-bolder font-size-sm"
                                        onClick={toggleModal("showNewOrderModal")}
                                    >
                                        <span className="svg-icon svg-icon-md svg-icon-white">
                                            <SVG
                                                title=" "
                                                src={toAbsoluteUrl("/media/svg/icons/Shopping/Cart1.svg")}
                                            />
                                        </span>
                                        New Order
                                    </button>
                                    <FilterGroup.Content>
                                        {({ filters }) => (
                                            <button
                                                type="button"
                                                className="btn btn-primary ml-4"
                                                disabled={isLoadingReport}
                                                onClick={() =>
                                                    downloadOrdersReport({
                                                        userId: accountDetails.userId,
                                                        userGroup: accountDetails.userGroup,
                                                        provider: accountDetails.partner,
                                                        liveData: true,
                                                        startDate: filters?.startDate || "",
                                                        endDate: filters?.endDate || "",
                                                        format: "csv",
                                                        status: filters?.status || "",
                                                    })
                                                }
                                            >
                                                Export CSV
                                            </button>
                                        )}
                                    </FilterGroup.Content>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex flex-column align-items-start w-100 pl-10">
                            <FilterGroup.DateRange className={"mb-4 mt-2"} />
                            <div className={"mr-4 mt-4"}>
                                <FilterGroup.Filter name={"status"} label={"Status"}>
                                    <FilterGroup.Option value="">All</FilterGroup.Option>
                                    <FilterGroup.Option value="PENDING">PENDING</FilterGroup.Option>
                                    <FilterGroup.Option value="PARTIAL_FILL">PARTIAL_FILL</FilterGroup.Option>
                                    <FilterGroup.Option value="ON_HOLD">ON HOLD</FilterGroup.Option>
                                    <FilterGroup.Option value="MANUAL_PROCESSING">MANUAL PROCESSING</FilterGroup.Option>
                                    <FilterGroup.Option value="CANCELLED">CANCELLED</FilterGroup.Option>
                                    <FilterGroup.Option value="REJECTED">REJECTED</FilterGroup.Option>
                                    <FilterGroup.Option value="CANCELLING">CANCELLING</FilterGroup.Option>
                                    <FilterGroup.Option value="PROCESSING">PROCESSING</FilterGroup.Option>
                                    <FilterGroup.Option value="COMPLETED">COMPLETED</FilterGroup.Option>
                                    <FilterGroup.Option value="FAILED">FAILED</FilterGroup.Option>
                                </FilterGroup.Filter>
                            </div>
                        </div>

                        {/* <!--end::Header--> */}
                        {/* <!--begin::Body--> */}
                        <div className="card-body pt-0">
                            <FilterGroup.Content>
                                {({ filters }) => {
                                    const filteredOrders = accountDetails.orders.filter(order => {
                                        const orderDate = new Date(order.created);
                                        const startDate = filters?.startDate ? new Date(filters.startDate) : null;
                                        const endDate = filters?.endDate ? new Date(filters.endDate).setHours(23, 59, 59, 999) : null;
                                        const orderStatus = filters?.status;

                                        return (
                                            (!startDate || orderDate >= startDate) &&
                                            (!endDate || orderDate <= endDate) &&
                                            (!orderStatus || order.status === orderStatus)
                                        );
                                    });

                                    return (

                                        <SimpleTable
                                            data={filteredOrders}
                                            columns={[
                                                { title: "ACTIVITY", format: formatActivity },
                                                { title: "ID", key: "id" },
                                                { title: "REFERENCE", key: "refid" },
                                                { title: "QUANTITY", key: "quantity" },
                                                { title: "AMOUNT", format: row => formatAmount(row.currency, row.amount) },
                                                { title: "FEE", format: row => formatAmount(row.currency, Math.abs(row.total - row.amount)) },
                                                { title: "TOTAL", format: row => formatAmount(row.currency, row.total) },
                                                { title: "STATUS", format: row => formatStatus(row.status) },
                                                { title: "BALANCE BEFORE", format: row => formatAmount(row.currency, row.accountBalance) },
                                                { title: "BALANCE AFTER", format: row => formatAmount(row.currency, getOrderBalanceAfter(row)) },
                                                { title: "DATE", format: row => formatDate(row.created) },
                                                { title: "", format: orderAction }
                                            ]}
                                        />
                                        );
                            }}
                        </FilterGroup.Content>
                        </div>
                        {/* <!--end::Body--> */}
                    </FilterGroup>
                </div>
                {/* <!--end::List Widget 14--> */}
            </div>

            <ViewOrderModal {...orderModal} onHide={toggleModal("showViewOrderModal")} />
            <NewOrderModal {...props} {...orderModal} provider={accountDetails.partner}
                onHide={toggleModal("showNewOrderModal")} refreshOrders={refreshOrders} />
            <CancelOrderModal {...orderModal} provider={accountDetails.partner}
                onHide={toggleModal("showCancelOrderModal")} refreshOrders={refreshOrders} />
            {
                orderModal.showEditOrderModal &&
                <EditOrderModal
                    {...orderModal}
                    onHide={toggleModal("showEditOrderModal")} refreshOrders={refreshOrders} />
            }
        </div>
    )
}

export default Orders;